import { useEffect } from 'react';
import '../styles/Home.css';
import Intro from './Intro';
import RoadMap from './RoadMap';
import Map from './Map';
import heroImage from '../images/Hero.avif';

const content = {
  swe: {
    heroTag: 'Rekrytering, bemanning och interim',
    title: 'Rätt kompetens för rätt uppdrag',
    subtitle:
      'Vi hjälper företag med rekrytering, bemanning och interim, från ingenjörer och IT-specialister till ekonomer och andra roller.',
    primary: 'Hitta kompetens',
    secondary: 'Se lediga uppdrag',
    servicesTitle: 'Våra tjänster',
    servicesSubtitle:
      'Rekrytering, bemanning och interim med fokus på rätt kompetens inom teknik, ingenjörskap, ekonomi och fler specialistområden.',
    competenceTitle: 'Kompetensområden',
    competenceText:
      'Vi arbetar brett med specialistprofiler, bland annat ingenjörer och teknik, ekonomi och finans, systemutveckling och IT-relaterade roller.',
    services: [
      {
        title: 'Rekrytering',
        desc: 'Tillsättning av roller där ni vill anställa direkt och bygga långsiktigt.',
        routeKey: 'serviceRex'
      },
      {
        title: 'Bemanning',
        desc: 'Rätt specialist till er verksamhet när ni behöver förstärkning under en period.',
        routeKey: 'serviceBem'
      },
      {
        title: 'Interimlösningar',
        desc: 'Erfarna profiler som kliver in och leder när läget kräver snabb och tydlig förstärkning.',
        routeKey: 'serviceInterim'
      }
    ],
    learnMore: 'Läs mer'
  },
  eng: {
    heroTag: 'Recruitment, staffing and interim',
    title: 'The right expertise for the right assignments',
    subtitle:
      'We support companies with recruitment, staffing and interim roles, from engineers and IT specialists to finance professionals and more.',
    primary: 'Find experts',
    secondary: 'See open roles',
    servicesTitle: 'Our Services',
    servicesSubtitle:
      'Recruitment, staffing and interim with a focus on the right competence in technology, engineering, finance and more specialist areas.',
    competenceTitle: 'Areas of Expertise',
    competenceText:
      'We work broadly with specialist profiles, including engineering and technology, finance and accounting, software development and IT-related roles.',
    services: [
      {
        title: 'Recruitment',
        desc: 'Direct-hire roles when you want to employ long term and build your team.',
        routeKey: 'serviceRex'
      },
      {
        title: 'Staffing',
        desc: 'The right specialist for your organisation when you need extra capacity for a period.',
        routeKey: 'serviceBem'
      },
      {
        title: 'Interim Solutions',
        desc: 'Senior professionals who step in and lead when you need fast, decisive support.',
        routeKey: 'serviceInterim'
      }
    ],
    learnMore: 'Learn more'
  }
};

function Home({ lang = 'swe', setPage, onRedirectToJobs }) {
  const text = content[lang] || content.swe;
  const labels = lang === 'swe'
    ? { kicker: 'Var vi skapar störst effekt' }
    : { kicker: 'Where we create the most impact' };

  useEffect(() => {
    const preload = document.createElement('link');
    preload.rel = 'preload';
    preload.as = 'image';
    preload.href = heroImage;
    preload.type = 'image/avif';
    document.head.appendChild(preload);

    return () => {
      document.head.removeChild(preload);
    };
  }, []);

  return (
    <>
      <section className="hero" id="home">
        <img
          className="hero-image"
          src={heroImage}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <p className="hero-tag">{text.heroTag}</p>
          <h1 className="hero-title">{text.title}</h1>
          <p className="hero-subtitle">{text.subtitle}</p>
          <div className="hero-buttons">
            <button className="home-primary-btn" onClick={() => setPage('competence')}>{text.primary}</button>
            <button
              className="home-secondary-btn"
              onClick={() => {
                if (onRedirectToJobs) {
                  onRedirectToJobs();
                  return;
                }
                setPage('jobs');
              }}
            >
              {text.secondary}
            </button>
          </div>
        </div>
      </section>

      <Intro lang={lang} onNavigate={setPage} />

      <section className="services-section" id="services">
        <div className="services-header">
          <p className="services-kicker">{labels.kicker}</p>
          <h2 className="services-title">{text.servicesTitle}</h2>
          <p className="services-subtitle">{text.servicesSubtitle}</p>
        </div>
        <div className="services-grid">
          {text.services.map((service, index) => (
            <article className="service-card" key={service.title}>
              <div className="service-top">
                <span className="service-badge">{`0${index + 1}`}</span>
              </div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.desc}</p>
              <button type="button" className="service-link" onClick={() => setPage(service.routeKey)}>
                <span>{text.learnMore}</span>
              </button>
            </article>
          ))}
        </div>

        <div className="services-header" style={{ marginTop: '1.6rem' }}>
          <h3 className="services-title" style={{ fontSize: 'clamp(1.25rem, 3vw, 1.65rem)' }}>
            {text.competenceTitle}
          </h3>
          <p className="services-subtitle">{text.competenceText}</p>
        </div>
      </section>

      <Map lang={lang} setPage={setPage} />

      <RoadMap lang={lang} />
    </>
  );
}

export default Home;
